import React, { useEffect, useMemo, useRef, useState } from "react";

/*
 * SpinningCoin — compact spinning-coin visual, reusable inline.
 *
 * Renders three stacked images (edge/front/back) and animates a face-flip
 * horizontally via `scaleX` (same 3D-ish trick as CoinReveal, kept lightweight).
 *
 * Props:
 *   size         — px, default 32
 *   spinTrigger  — value that, when changed, restarts the spin
 *   durationMs   — spin duration, default 1400
 *   autoStart    — spin on mount, default true
 *   onClickToSpin — if true, clicking the coin re-spins
 *
 * Coin art paths mirror CoinReveal.jsx.
 */

const ASSETS = {
  edge:  "/rewards/eros-coin-edge.png",
  front: "/rewards/eros-coin2.png",
  back:  "/rewards/eros-coin3.png",
};

function faceVisual(d) {
  return d <= 0.20
    ? { scaleX: 1 - (d / 0.20) * 0.7, opacity: 1 }
    : { scaleX: 0.05, opacity: 0 };
}

const SpinningCoin = ({
  size = 32,
  spinTrigger = 0,
  durationMs = 1400,
  autoStart = true,
  onClickToSpin = true,
  className = "",
}) => {
  const rafRef = useRef(null);
  const [face, setFace] = useState({
    frontScaleX: 1, frontOpacity: 1,
    backScaleX: 0.05, backOpacity: 0,
  });
  const [spinning, setSpinning] = useState(false);

  const startSpin = useMemo(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const startTs = performance.now();
      setSpinning(true);
      // Land exactly on a full face: pick an integer number of half-flips.
      // targetFrac 0 = front face, 0.5 = back face; both give scaleX ≈ 1 at rest.
      const totalRevolutions = 3 + Math.floor(Math.random() * 2); // 3 or 4 full spins
      const targetFrac = Math.random() > 0.5 ? 0 : 0.5;

      const step = (now) => {
        const t = Math.min(1, (now - startTs) / durationMs);
        const eased = 1 - Math.pow(1 - t, 3);
        // At eased=1, spinTotal = totalRevolutions + targetFrac (frac lands exactly on target).
        const spinTotal = eased * (totalRevolutions + targetFrac);
        const frac = spinTotal - Math.floor(spinTotal);
        const fv = faceVisual(Math.min(frac, 1 - frac));
        const bv = faceVisual(Math.abs(frac - 0.5));
        setFace({
          frontScaleX: fv.scaleX, frontOpacity: fv.opacity,
          backScaleX: bv.scaleX,  backOpacity: bv.opacity,
        });
        if (t < 1) {
          rafRef.current = requestAnimationFrame(step);
        } else {
          rafRef.current = null;
          setSpinning(false);
        }
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [durationMs]
  );

  // Initial + re-triggered spins
  useEffect(() => {
    if (autoStart || spinTrigger !== 0) startSpin();
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [spinTrigger, autoStart, startSpin]);

  const handleClick = () => {
    if (onClickToSpin && !spinning) startSpin();
  };

  const dim = `${size}px`;
  return (
    <div
      className={`inline-block relative ${className}`}
      style={{ width: dim, height: dim, cursor: onClickToSpin ? "pointer" : "default" }}
      onClick={handleClick}
      role={onClickToSpin ? "button" : undefined}
      aria-label={onClickToSpin ? "Spin coin" : "Coin"}
      data-testid="spinning-coin"
    >
      <img
        src={ASSETS.edge}
        alt=""
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          filter: "drop-shadow(0 0 6px rgba(212,175,106,0.4))",
        }}
        draggable={false}
      />
      <img
        src={ASSETS.front}
        alt=""
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          transform: `scaleX(${face.frontScaleX})`,
          opacity: face.frontOpacity,
          transformOrigin: "center",
          willChange: "transform, opacity",
        }}
        draggable={false}
      />
      <img
        src={ASSETS.back}
        alt=""
        style={{
          position: "absolute", inset: 0, width: "100%", height: "100%",
          transform: `scaleX(${face.backScaleX})`,
          opacity: face.backOpacity,
          transformOrigin: "center",
          willChange: "transform, opacity",
        }}
        draggable={false}
      />
    </div>
  );
};

export default SpinningCoin;
